import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { updateOrRemoveQueryParam } from "@circle-react/helpers/urlHelpers";
import { useAdminPaywallCharge } from "@circle-react/hooks/paywalls/useAdminPaywallCharge";
import { useRouterQueryParams } from "@circle-react/hooks/useRouterQueryParams";
import { useFilterUrl } from "@circle-react-shared/Filter";
import {
  allFilterTranslations,
  normalizeFilters,
} from "../../Filters/useFilters";
import { getPaginationLimitThreshold } from "../../utils";
import { TABS, type TabType, statusByTab } from "../constants";

const defaultOrder = { created_at: "desc" } as const;

interface QueryParams {
  [key: string]: unknown;
  page?: string;
  search_key?: string;
  status?: string;
  tab?: string;
}

type ChargesDataByTab = Record<
  TabType,
  ReturnType<typeof useAdminPaywallCharge>
>;

export const usePaywallChargesAdmin = () => {
  const history = useHistory();
  const queryParams = useRouterQueryParams() as QueryParams;
  const { removeQueryParams } = useFilterUrl();

  const {
    tab = TABS.ALL,
    search_key = "",
    page: pageParam = 1,
    status,
  } = queryParams;

  const [page, setPage] = useState(getPaginationLimitThreshold(pageParam));
  const [activeFilterTab, setActiveFilterTab] = useState<TabType>(
    Object.values(TABS).includes(tab as TabType) ? (tab as TabType) : TABS.ALL,
  );
  const [searchKey, setSearchKey] = useState(search_key);

  const getSearchKey = (
    currentTab: TabType,
    tabTarget: TabType,
    searchKey: string,
  ): string => (currentTab === tabTarget ? searchKey : "");

  const updateQueryParam = (keyQueryParam: string, value: string): void => {
    const newQueryParams = updateOrRemoveQueryParam(keyQueryParam, value);
    history.push(`?${newQueryParams}`);
  };

  const clearAllFilters = (): void => {
    const filterKeys = Object.keys(allFilterTranslations.charges);
    removeQueryParams(filterKeys);
  };

  const updatePage = (value: number): void => {
    updateQueryParam("page", String(value));
    setPage(value);
  };

  useEffect(() => {
    updateQueryParam("tab", activeFilterTab);
    if (!status) {
      const statusValue = statusByTab[activeFilterTab];
      if (statusValue) {
        updateQueryParam("status", statusValue);
      }
    }
  }, []);

  const setActiveFilter = (tab: TabType): void => {
    clearAllFilters();
    setActiveFilterTab(tab);
    updateQueryParam("status", statusByTab[tab] || "");
    updateQueryParam("tab", tab);
    setSearchKey("");
  };

  const allChargesData = useAdminPaywallCharge({
    searchKeyParams: getSearchKey(activeFilterTab, TABS.ALL, searchKey),
    orderParam: defaultOrder,
    filters: normalizeFilters(
      {
        ...queryParams,
      },
      allFilterTranslations.charges,
    ),
    page,
    setPage: updatePage,
    enabled: activeFilterTab === TABS.ALL,
  });

  const paidChargesData = useAdminPaywallCharge({
    searchKeyParams: getSearchKey(activeFilterTab, TABS.PAID, searchKey),
    orderParam: defaultOrder,
    filters: normalizeFilters(
      {
        display_status: statusByTab[TABS.PAID],
        ...queryParams,
      },
      allFilterTranslations.charges,
    ),
    page,
    setPage: updatePage,
    enabled: activeFilterTab === TABS.PAID,
  });

  const processingChargesData = useAdminPaywallCharge({
    searchKeyParams: getSearchKey(activeFilterTab, TABS.PROCESSING, searchKey),
    orderParam: defaultOrder,
    filters: normalizeFilters(
      {
        display_status: statusByTab[TABS.PROCESSING],
        ...queryParams,
      },
      allFilterTranslations.charges,
    ),
    page,
    setPage: updatePage,
    enabled: activeFilterTab === TABS.PROCESSING,
  });

  const refundedChargesData = useAdminPaywallCharge({
    searchKeyParams: getSearchKey(activeFilterTab, TABS.REFUNDED, searchKey),
    orderParam: defaultOrder,
    filters: normalizeFilters(
      {
        display_status: statusByTab[TABS.REFUNDED],
        ...queryParams,
      },
      allFilterTranslations.charges,
    ),
    page,
    setPage: updatePage,
    enabled: activeFilterTab === TABS.REFUNDED,
  });

  const failedChargesData = useAdminPaywallCharge({
    searchKeyParams: getSearchKey(activeFilterTab, TABS.FAILED, searchKey),
    orderParam: defaultOrder,
    filters: normalizeFilters(
      {
        display_status: statusByTab[TABS.FAILED],
        ...queryParams,
      },
      allFilterTranslations.charges,
    ),
    page,
    setPage: updatePage,
    enabled: activeFilterTab === TABS.FAILED,
  });

  const resetAllChargesToFirstPage = (): void => {
    updatePage(1);
    allChargesData.setOrder(defaultOrder);
    paidChargesData.setOrder(defaultOrder);
    processingChargesData.setOrder(defaultOrder);
    refundedChargesData.setOrder(defaultOrder);
    failedChargesData.setOrder(defaultOrder);
  };

  const queryClient = useQueryClient();
  const refetchAllCharges = (): void => {
    queryClient.clear();
    void allChargesData.refetchCharges();
    void paidChargesData.refetchCharges();
    void processingChargesData.refetchCharges();
    void refundedChargesData.refetchCharges();
    void failedChargesData.refetchCharges();
  };

  const chargesDataByTab: ChargesDataByTab = {
    [TABS.ALL]: allChargesData,
    [TABS.PAID]: paidChargesData,
    [TABS.PROCESSING]: processingChargesData,
    [TABS.REFUNDED]: refundedChargesData,
    [TABS.FAILED]: failedChargesData,
  };

  return {
    chargesDataByTab,
    activeFilterTab,
    setActiveFilterTab: setActiveFilter,
    setSearchKey,
    refetchAllCharges,
    resetAllChargesToFirstPage,
    isLoadingCharges: allChargesData.isLoadingCharges,
  };
};
